import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { ArrowLeftRight, TrendingUp, RefreshCw, Globe2 } from 'lucide-react';

const POPULAR = ['USD', 'EUR', 'INR', 'GBP', 'AED', 'AUD', 'JPY', 'SGD', 'CAD', 'CHF', 'CNY', 'SAR'];
const FLAG = {
  USD: '🇺🇸', EUR: '🇪🇺', INR: '🇮🇳', GBP: '🇬🇧', AED: '🇦🇪', AUD: '🇦🇺',
  JPY: '🇯🇵', SGD: '🇸🇬', CAD: '🇨🇦', CHF: '🇨🇭', CNY: '🇨🇳', SAR: '🇸🇦',
  THB: '🇹🇭', MYR: '🇲🇾', HKD: '🇭🇰', NZD: '🇳🇿', ZAR: '🇿🇦', NOK: '🇳🇴',
  SEK: '🇸🇪', KRW: '🇰🇷', IDR: '🇮🇩', PHP: '🇵🇭', BRL: '🇧🇷', MXN: '🇲🇽',
  TRY: '🇹🇷', RUB: '🇷🇺', PLN: '🇵🇱', DKK: '🇩🇰', CZK: '🇨🇿', HUF: '🇭🇺',
};

const CurrencyConverter = () => {
  const [symbols, setSymbols] = useState(null);
  const [from, setFrom] = useState('USD');
  const [to, setTo] = useState('INR');
  const [amount, setAmount] = useState(100);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);
  const debounce = useRef(null);

  useEffect(() => {
    axios.get(`${API}/fx/symbols`).then((r) => setSymbols(r.data || {})).catch(() => setSymbols({}));
  }, []);

  const convert = async () => {
    if (!amount || amount <= 0) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/fx/convert`, { params: { from, to, amount } });
      setResult(r.data);
      setFetchedAt(r.data.fetched_at);
    } catch (e) {
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(convert, 250);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, amount]);

  const swap = () => {
    setFrom(to);
    setTo(from);
  };

  const allCodes = symbols ? Object.keys(symbols).sort() : POPULAR;
  const fmtTime = fetchedAt ? new Date(fetchedAt).toUTCString().slice(17, 22) : '--:--';

  return (
    <section className="fx-converter" data-testid="fx-converter">
      <div className="fx-wrap">
        <div className="fx-head">
          <div className="fx-eyebrow"><Globe2 size={14} /> LIVE FX • mid-market</div>
          <h3 className="fx-title">Send Money? Know the real rate.</h3>
          <p className="fx-sub">Convert across 30+ currencies at ECB mid-market rates — the same one banks use. No hidden margins, no markups.</p>
        </div>

        <div className="fx-card">
          <div className="fx-row">
            <div className="fx-field">
              <label>You send</label>
              <div className="fx-input-grp">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={amount}
                  onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                  data-testid="fx-amount-input"
                />
                <select value={from} onChange={(e) => setFrom(e.target.value)} data-testid="fx-from-select">
                  {allCodes.map((c) => (
                    <option key={c} value={c}>{FLAG[c] || '🌐'}  {c}</option>
                  ))}
                </select>
              </div>
            </div>

            <button onClick={swap} className="fx-swap" aria-label="Swap currencies" data-testid="fx-swap-btn">
              <ArrowLeftRight size={18} />
            </button>

            <div className="fx-field">
              <label>They receive</label>
              <div className="fx-input-grp">
                <input
                  type="text"
                  readOnly
                  value={result ? Number(result.converted).toLocaleString('en-IN', { maximumFractionDigits: 4 }) : '—'}
                  data-testid="fx-result-input"
                />
                <select value={to} onChange={(e) => setTo(e.target.value)} data-testid="fx-to-select">
                  {allCodes.map((c) => (
                    <option key={c} value={c}>{FLAG[c] || '🌐'}  {c}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="fx-foot">
            <div className="fx-rate" data-testid="fx-rate-line">
              {loading ? (
                <span className="fx-loading"><RefreshCw size={12} className="spin" /> updating…</span>
              ) : result ? (
                <>
                  <strong>1 {result.from} = {Number(result.rate).toLocaleString('en-IN', { maximumFractionDigits: 4 })} {result.to}</strong>
                  <span className="fx-time"><TrendingUp size={12} /> Mid-market rate at {fmtTime} UTC</span>
                </>
              ) : (
                <span style={{ color: '#94A3B8' }}>Enter an amount to begin</span>
              )}
            </div>
            <button className="fx-track" onClick={convert} data-testid="fx-refresh-btn">
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>

        <div className="fx-quick">
          {['USD-INR', 'EUR-INR', 'GBP-INR', 'AED-INR', 'INR-USD'].map((pair) => {
            const [a, b] = pair.split('-');
            const active = from === a && to === b;
            return (
              <button
                key={pair}
                className={`fx-chip ${active ? 'active' : ''}`}
                onClick={() => { setFrom(a); setTo(b); }}
                data-testid={`fx-quick-${pair}`}
              >
                {FLAG[a]} {a} → {FLAG[b]} {b}
              </button>
            );
          })}
        </div>
      </div>

      <style>{`
        .fx-converter { padding: 4rem 2rem; background: linear-gradient(180deg, #FFF7ED 0%, #FFFFFF 100%); }
        .fx-wrap { max-width: 1100px; margin: 0 auto; }
        .fx-head { text-align: center; margin-bottom: 2rem; }
        .fx-eyebrow { display: inline-flex; align-items: center; gap: 6px; font-size: 0.72rem; font-weight: 800; letter-spacing: 0.12em; color: #C2410C; background: #FFEDD5; padding: 6px 12px; border-radius: 999px; }
        .fx-title { font-family: 'Outfit', sans-serif; font-size: clamp(1.6rem, 3.5vw, 2.4rem); font-weight: 800; color: #0F172A; margin: 12px 0 6px; }
        .fx-sub { color: #475569; max-width: 560px; margin: 0 auto; font-size: 0.95rem; }
        .fx-card { background: white; border-radius: 18px; padding: 1.75rem; box-shadow: 0 10px 40px rgba(0,0,0,0.06), 0 0 0 1px #FDBA7430; }
        .fx-row { display: grid; grid-template-columns: 1fr auto 1fr; gap: 1rem; align-items: end; }
        .fx-field label { display: block; font-size: 0.72rem; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 8px; }
        .fx-input-grp { display: flex; align-items: stretch; background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 12px; overflow: hidden; transition: border-color .2s ease; }
        .fx-input-grp:focus-within { border-color: #FF6B35; box-shadow: 0 0 0 4px rgba(255,107,53,0.10); }
        .fx-input-grp input { flex: 1; padding: 1rem 1rem; background: transparent; border: 0; outline: none; font-size: 1.4rem; font-weight: 700; color: #0F172A; min-width: 0; font-family: 'Outfit', sans-serif; }
        .fx-input-grp select { padding: 0 1rem; background: white; border: 0; border-left: 1px solid #E2E8F0; outline: none; font-weight: 700; color: #0F172A; cursor: pointer; font-size: 0.95rem; }
        .fx-swap { background: linear-gradient(135deg, #FF6B35 0%, #F97316 100%); color: white; border: 0; width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 6px 18px rgba(255,107,53,0.35); align-self: end; margin-bottom: 4px; transition: transform .2s ease; }
        .fx-swap:hover { transform: rotate(180deg); }
        .fx-foot { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem; margin-top: 1.25rem; padding-top: 1.25rem; border-top: 1px dashed #E2E8F0; }
        .fx-rate { font-size: 1rem; color: #0F172A; display: flex; flex-direction: column; gap: 4px; }
        .fx-rate strong { font-weight: 700; }
        .fx-time { font-size: 0.78rem; color: #64748B; display: inline-flex; align-items: center; gap: 4px; }
        .fx-loading { display: inline-flex; align-items: center; gap: 6px; color: #64748B; }
        .fx-track { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; background: white; border: 1px solid #E2E8F0; border-radius: 8px; color: #475569; font-size: 0.85rem; font-weight: 600; cursor: pointer; transition: all .2s ease; }
        .fx-track:hover { background: #F8FAFC; border-color: #FF6B35; color: #FF6B35; }
        .fx-quick { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 1.25rem; justify-content: center; }
        .fx-chip { padding: 8px 14px; background: white; border: 1px solid #E2E8F0; border-radius: 999px; font-size: 0.82rem; font-weight: 600; color: #475569; cursor: pointer; transition: all .2s ease; }
        .fx-chip:hover { border-color: #FDBA74; }
        .fx-chip.active { background: linear-gradient(135deg, #FF6B35 0%, #F97316 100%); color: white; border-color: transparent; box-shadow: 0 4px 12px rgba(255,107,53,0.30); }
        .spin { animation: fx-spin 1s linear infinite; }
        @keyframes fx-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        @media (max-width: 720px) {
          .fx-row { grid-template-columns: 1fr; }
          .fx-swap { transform: rotate(90deg); justify-self: center; }
          .fx-swap:hover { transform: rotate(270deg); }
        }
      `}</style>
    </section>
  );
};

export default CurrencyConverter;
